import { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Typography, Tabs, Button, Form, Input, message,
  Space, Statistic, Row, Col, Popconfirm, Alert, Modal, Switch, Badge,
  Upload, Avatar, Descriptions, List,
} from 'antd';
import dayjs from 'dayjs';
import { LockOutlined, WechatOutlined } from '@ant-design/icons';
import client from '../api/client';
import useAuthStore from '../stores/authStore';
import SmsCodeInput from '../components/SmsCodeInput';
import MyTicketsTab from '../components/MyTicketsTab';
import PlanCard from '../components/payment/PlanCard';
import { fetchPayOrders, fetchMyCoupons, redeemCoupon, type PayOrder, type MyCoupon } from '../api/pay';
import { unbindWechat, fetchWechatBindUrl } from '../api/wechat';

interface SessionItem {
  sessionId: string;
  sessionType: 'edit' | 'view';
  deviceInfo?: string;
  ip?: string;
  userAgent?: string;
  createAt: number;
  lastActiveAt: number;
  expireAt: number;
}

function fmtDuration(sec?: number) {
  if (sec == null) return '-';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}小时${m}分`;
  if (m > 0) return `${m}分${s}秒`;
  return `${s}秒`;
}

/** D1（2026-10-03）：个人资料卡——头像（微信头像默认/可上传替换）+ 昵称/性别/地区（来自微信公开信息） */
function ProfileTab() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [uploading, setUploading] = useState(false);

  const displayName = user?.wechatNickname || user?.username || '-';
  const avatar = user?.avatar || user?.wechatAvatar || '';
  const genderLabel: Record<string, string> = { male: '男', female: '女', unknown: '未知' };
  const gender = user?.wechatSex ? genderLabel[user.wechatSex] || '-' : '-';
  const region = [user?.wechatProvince, user?.wechatCity].filter(Boolean).join(' ') || '-';

  const refreshMe = async () => {
    try {
      const r = await client.get('/auth/me');
      const me = r.data?.user ?? r.data;
      if (me?.id) setUser(me);
    } catch {
      /* 刷新失败保持旧值 */
    }
  };

  const uploadAvatar = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await client.post('/auth/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) {
        await refreshMe();
        message.success('头像已更新');
      }
    } catch {
      message.error('头像上传失败（仅支持 png/jpg/webp，≤2MB）');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="profile-tab">
      <Space size={28} align="start" wrap>
        <div style={{ textAlign: 'center' }}>
          <Upload
            showUploadList={false}
            accept="image/png,image/jpeg,image/webp"
            beforeUpload={(f) => {
              void uploadAvatar(f as unknown as File);
              return false;
            }}
          >
            <Avatar
              size={88}
              src={avatar || undefined}
              style={{ cursor: 'pointer', background: '#1677ff', fontSize: 34 }}
            >
              {(displayName || '?').slice(0, 1).toUpperCase()}
            </Avatar>
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
              {uploading ? '上传中…' : '点击更换头像'}
            </div>
          </Upload>
        </div>
        <Descriptions column={1} size="small" style={{ flex: 1, minWidth: 320 }}>
          <Descriptions.Item label="昵称">
            {displayName}
            {user?.wechatNickname ? <Tag color="green" style={{ marginLeft: 8 }}>来自微信</Tag> : null}
          </Descriptions.Item>
          <Descriptions.Item label="性别">{gender}</Descriptions.Item>
          <Descriptions.Item label="地区">{region}</Descriptions.Item>
          <Descriptions.Item label="手机号">{user?.phoneNumber || '未绑定'}</Descriptions.Item>
          <Descriptions.Item label="联系邮箱">{user?.email || '未绑定'}</Descriptions.Item>
          <Descriptions.Item label="微信">
            {user?.wechatUnionid ? <Tag color="green">已绑定</Tag> : <Tag>未绑定</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Space>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
        昵称/性别/地区来自微信公开信息（绑定微信后展示）；上传自定义头像后将优先展示。
      </Typography.Paragraph>
    </div>
  );
}

/** F（2026-10-03）：我的优惠券——可用/占用/已用/过期 + 兑换码领取 */
function MyCouponsTab() {
  const [items, setItems] = useState<MyCoupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchMyCoupons();
      setItems(r.items ?? []);
    } catch {
      message.error('优惠券加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const doRedeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const r = await redeemCoupon(code.trim());
      message.success(`兑换成功：${r.name}`);
      setCode('');
      await load();
    } catch (e: unknown) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message || '兑换失败');
    } finally {
      setRedeeming(false);
    }
  };

  const STATUS: Record<string, { label: string; color: string }> = {
    available: { label: '可用', color: 'green' },
    locked: { label: '下单占用', color: 'orange' },
    used: { label: '已使用', color: 'default' },
    expired: { label: '已过期', color: 'default' },
    void: { label: '已作废', color: 'red' },
  };

  return (
    <div data-testid="my-coupons-tab">
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="输入兑换码（如 DTW-XXXX-XXXX-XXXX）"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onPressEnter={() => void doRedeem()}
          style={{ width: 300 }}
        />
        <Button type="primary" loading={redeeming} onClick={() => void doRedeem()} data-testid="coupon-redeem">
          兑换
        </Button>
        <Button onClick={() => void load()} loading={loading}>刷新</Button>
      </Space>
      <List
        dataSource={items}
        loading={loading}
        locale={{ emptyText: '暂无优惠券（可输入兑换码领取，或等待运营发放）' }}
        renderItem={(c) => (
          <List.Item>
            <List.Item.Meta
              avatar={
                <Tag color={c.couponType === 'fixed' ? 'gold' : 'blue'} style={{ marginTop: 4, fontSize: 13 }}>
                  {c.couponType === 'fixed' ? `¥${c.faceValue}` : `${(c.discountRate * 10).toFixed(1)}折`}
                </Tag>
              }
              title={
                <Space wrap>
                  <span>{c.name}</span>
                  <Tag color={STATUS[c.status]?.color}>{STATUS[c.status]?.label || c.status}</Tag>
                  {c.couponType === 'discount' && c.maxDiscount > 0 ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>最高抵 ¥{c.maxDiscount}</Typography.Text>
                  ) : null}
                </Space>
              }
              description={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {c.minAmount > 0 ? `满 ¥${c.minAmount} 可用 · ` : ''}
                  {c.expireAt ? `有效期至 ${dayjs(c.expireAt).format('YYYY-MM-DD HH:mm')}` : '长期有效'}
                </Typography.Text>
              }
            />
          </List.Item>
        )}
      />
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
        优惠券仅限本平台消费抵扣，不可提现、转让或兑换现金；过期作废。下单时在支付弹窗选择使用。
      </Typography.Paragraph>
    </div>
  );
}

interface OnlineActivity {  online: boolean;
  lastActiveAt: number | null;
  device?: string;
  ip?: string;
  sessionCount: number;
  staleThresholdMs?: number;
}

/** C1（2026-10-03）：个人中心「在线状态」——只保留实时状态（在线/离线）+ 今日时长，
 *  详细登录明细移到中台用户中心（运营视角）。 */
function OnlineStatusTab() {
  const [activity, setActivity] = useState<OnlineActivity | null>(null);
  const [daily, setDaily] = useState<{
    loginCount: number;
    failedCount: number;
    totalDurationSeconds: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, d] = await Promise.all([
        client.get('/auth/online-status'),
        client.get('/auth/logs/daily'),
      ]);
      setActivity(a.data);
      setDaily(d.data);
    } catch {
      message.error('加载在线状态失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const online = !!activity?.online;
  return (
    <div data-testid="online-status-tab">
      <Card size="small" loading={loading && !activity} style={{ marginBottom: 16 }}>
        <Space size={16} align="center" wrap>
          <Badge status={online ? 'success' : 'default'} />
          <Typography.Title level={4} style={{ margin: 0 }} data-testid="online-status-text">
            {activity ? (online ? '在线' : '离线') : '-'}
          </Typography.Title>
          <Typography.Text type="secondary">
            {online ? '当前有活跃会话（最近 5 分钟内有操作）' : '当前无活跃会话'}
          </Typography.Text>
          <Button size="small" onClick={() => void load()} loading={loading}>刷新</Button>
        </Space>
      </Card>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small"><Statistic title="今日在线时长" value={fmtDuration(daily?.totalDurationSeconds ?? 0)} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="今日登录次数" value={daily?.loginCount ?? 0} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="今日失败次数" value={daily?.failedCount ?? 0} valueStyle={{ color: '#cf1322' }} /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="有效会话数" value={activity?.sessionCount ?? 0} /></Card>
        </Col>
      </Row>
      <Card size="small" title="当前设备">
        <Space orientation="vertical" size={2}>
          <Typography.Text>设备：{activity?.device || '-'}</Typography.Text>
          <Typography.Text>IP：{activity?.ip || '-'}</Typography.Text>
          <Typography.Text>
            最近活跃：{activity?.lastActiveAt ? dayjs(activity.lastActiveAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </Typography.Text>
        </Space>
      </Card>
    </div>
  );
}

function ChangePhoneTab() {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const user = useAuthStore((s) => s.user);
  const newPhone = Form.useWatch('newPhone', form) || '';
  const oldPhone = Form.useWatch('oldPhone', form) || '';
  // 是否已绑定手机号：未绑定=首次绑定（仅新号验证码）；已绑定=换绑（新旧双验证码）
  const hasPhone = !!user?.phoneNumber;

  const onFinish = async (values: { oldPhoneSmsCode?: string; newPhone: string; newPhoneSmsCode: string }) => {
    setLoading(true);
    try {
      const res = await client.post('/auth/change-phone', {
        oldPhoneSmsCode: values.oldPhoneSmsCode,
        newPhone: values.newPhone,
        newPhoneSmsCode: values.newPhoneSmsCode,
      });
      // 绑定/换绑成功 = 安全策略全会话失效（后端已踢），前端主动提示并跳登录，
      // 避免等待 401 拦截导致的整页白屏跳转
      message.success(hasPhone ? '手机号更换成功，请使用新手机号重新登录' : '手机号绑定成功，请使用手机号重新登录');
      form.resetFields();
      // 延迟 1.5s 让用户看到成功提示后跳转（不经过 401 拦截的白屏路径）
      setTimeout(() => {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }, 1500);
      return res.data;
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || '操作失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} onFinish={onFinish} layout="vertical" style={{ maxWidth: 460 }}>
      {hasPhone ? (
        <>
          <Form.Item
            name="oldPhone"
            rules={[
              { required: true, message: '请输入当前手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
            label="当前手机号（需验证码确认）"
          >
            <Input placeholder="当前手机号" maxLength={11} />
          </Form.Item>
          <Form.Item name="oldPhoneSmsCode" rules={[{ required: true, message: '请输入验证码' }]}>
            <SmsCodeInput scene="change_phone" phoneNumber={oldPhone} size="middle" />
          </Form.Item>
        </>
      ) : (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          您的账号尚未绑定手机号。绑定手机号后即可使用【手机号 + 验证码】注册登录相关功能（短信验证码登录、找回密码等）。
        </Typography.Paragraph>
      )}
      <Form.Item
        name="newPhone"
        rules={[
          { required: true, message: hasPhone ? '请输入新手机号' : '请输入手机号' },
          { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
        ]}
        label={hasPhone ? '新手机号' : '手机号'}
      >
        <Input placeholder={hasPhone ? '新手机号' : '手机号'} maxLength={11} />
      </Form.Item>
      <Form.Item name="newPhoneSmsCode" rules={[{ required: true, message: '请输入验证码' }]}>
        <SmsCodeInput scene="change_phone" phoneNumber={newPhone} size="middle" />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={loading}>
        {hasPhone ? '确认更换' : '确认绑定'}
      </Button>
    </Form>
  );
}

/** §2.8：自助设置/修改密码（首次设置走短信验证码，已设置需旧密码） */
function SetPasswordTab() {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const phoneNumber = user?.phoneNumber || '';

  const onFinish = async (values: {
    smsCode?: string;
    oldPassword?: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    setLoading(true);
    try {
      await client.post('/auth/set-password', {
        newPassword: values.newPassword,
        smsCode: values.smsCode || undefined,
        oldPassword: values.oldPassword || undefined,
      });
      message.success('密码设置成功，可使用手机号+密码登录');
      form.resetFields();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || '设置失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} onFinish={onFinish} layout="vertical" style={{ maxWidth: 460 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        设置密码后即可在登录页使用【手机号 + 密码】或【用户名 + 密码】登录（密码登录为备选通道，手机号验证码登录始终可用）。
      </Typography.Paragraph>
      <Form.Item
        name="oldPassword"
        rules={[{ min: 6, message: '密码长度至少 6 位' }]}
        label="当前密码（首次设置留空，仅修改时需要）"
      >
        <Input.Password prefix={<LockOutlined />} placeholder="首次设置请留空" autoComplete="current-password" />
      </Form.Item>
      {!user?.hasPassword && (
        <Form.Item name="smsCode" rules={[{ required: true, message: '请输入验证码' }]}>
          <SmsCodeInput scene="login" phoneNumber={phoneNumber} size="middle" />
        </Form.Item>
      )}
      <Form.Item
        name="newPassword"
        rules={[
          { required: true, message: '请输入新密码' },
          { min: 6, message: '密码长度至少 6 位' },
        ]}
        label="新密码"
      >
        <Input.Password prefix={<LockOutlined />} placeholder="至少 6 位" autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        dependencies={['newPassword']}
        rules={[
          { required: true, message: '请再次输入新密码' },
          ({ getFieldValue }) => ({
            validator: (_, v) =>
              !v || getFieldValue('newPassword') === v
                ? Promise.resolve()
                : Promise.reject(new Error('两次输入的密码不一致')),
          }),
        ]}
      >
        <Input.Password prefix={<LockOutlined />} placeholder="再次输入新密码" autoComplete="new-password" />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={loading}>
        设置密码
      </Button>
    </Form>
  );
}

function SessionsTab() {
  const [list, setList] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get('/auth/sessions');
      setList(res.data || []);
    } catch {
      message.error('加载登录设备失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kick = async (sessionId: string) => {
    try {
      await client.post(`/auth/sessions/${sessionId}/kick`);
      message.success('该设备已下线');
      load();
    } catch (err) {
      message.error((err as Error)?.message || '操作失败');
    }
  };

  const kickAll = async () => {
    try {
      const res = await client.post('/auth/sessions/kick-all');
      message.success(`已下线全部 ${res.data?.kicked ?? list.length} 个会话，请重新登录`);
      setTimeout(() => {
        window.location.href = '/login';
      }, 800);
    } catch (err) {
      message.error((err as Error)?.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '会话类型',
      dataIndex: 'sessionType',
      width: 110,
      render: (v: string) => <Tag color={v === 'edit' ? 'geekblue' : 'green'}>{v === 'edit' ? '编辑会话' : '预览会话'}</Tag>,
    },
    {
      title: '设备/浏览器',
      dataIndex: 'userAgent',
      ellipsis: true,
      render: (v?: string, row?: SessionItem) => row?.deviceInfo || v || '-',
    },
    { title: 'IP', dataIndex: 'ip', width: 130, render: (v?: string) => v || '-' },
    {
      title: '登录时间',
      dataIndex: 'createAt',
      width: 170,
      render: (v: number) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '最近活跃',
      dataIndex: 'lastActiveAt',
      width: 170,
      render: (v: number) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      width: 120,
      render: (_: unknown, row: SessionItem) => (
        <Button size="small" danger onClick={() => kick(row.sessionId)}>
          下线
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Button danger onClick={kickAll}>
          全部会话下线
        </Button>
        <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
          修改密码或更换手机号后，全部会话将自动失效
        </Typography.Text>
      </div>
      <Table
        rowKey="sessionId"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={list}
        pagination={false}
      />
    </div>
  );
}

export default function Account() {
  const { user } = useAuthStore();
  const [tab, setTab] = useState('profile');
  const [emailOpen, setEmailOpen] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailForm] = Form.useForm();

  /** T6：保存联系邮箱（业务通知收件地址，非登录凭证） */
  const handleSaveEmail = async (values: { email: string }) => {
    setSavingEmail(true);
    try {
      await client.post('/auth/set-email', { email: values.email });
      message.success('联系邮箱已保存');
      setEmailOpen(false);
      const me = await client.get('/auth/me');
      useAuthStore.getState().setUser(me.data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || '保存失败，请重试');
    } finally {
      setSavingEmail(false);
    }
  };

  /** T6：切换邮件通知开关 */
  const handleToggleNotice = async (checked: boolean) => {
    try {
      await client.post('/auth/notice-prefs', { emailNoticeEnabled: checked });
      const me = await client.get('/auth/me');
      useAuthStore.getState().setUser(me.data);
      message.success(checked ? '已开启邮件通知' : '已关闭邮件通知');
    } catch {
      message.error('设置失败，请重试');
    }
  };

  /** F-07：解绑微信（解绑后扫码将无法直接登录） */
  const handleUnbindWechat = async () => {
    try {
      await unbindWechat();
      message.success('已解绑微信');
      const me = await client.get('/auth/me');
      useAuthStore.getState().setUser(me.data);
    } catch (e: any) {
      message.error(e?.response?.data?.message || '解绑失败，请重试');
    }
  };

  /** F-07：已登录状态绑定微信（扫码后直接绑定当前账号，免短信） */
  const handleBindWechat = async () => {
    try {
      const res = await fetchWechatBindUrl();
      if (res.enabled && res.url) {
        window.location.href = res.url;
      } else {
        message.warning('微信登录未启用（请联系管理员配置）');
      }
    } catch {
      message.error('操作失败，请稍后重试');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        个人中心
      </Typography.Title>
      <Card style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={4}>
          <Typography.Text strong>用户名：{user?.username}</Typography.Text>
          {user?.phoneNumber ? (
            <Typography.Text>手机号：{user.phoneNumber}</Typography.Text>
          ) : (
            <Typography.Text type="secondary">手机号：未绑定</Typography.Text>
          )}
          <Typography.Text>
            联系邮箱：{user?.email || '未绑定'}
            <Button
              type="link"
              size="small"
              onClick={() => {
                emailForm.setFieldsValue({ email: user?.email || '' });
                setEmailOpen(true);
              }}
              data-testid="edit-email"
            >
              {user?.email ? '修改' : '绑定'}
            </Button>
          </Typography.Text>
          <Typography.Text>
            邮件通知：
            <Switch
              size="small"
              checked={user?.emailNoticeEnabled !== false}
              disabled={!user?.email}
              onChange={handleToggleNotice}
              checkedChildren="开"
              unCheckedChildren="关"
              data-testid="email-notice-switch"
            />
            <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              {user?.email ? '（账单/订阅到期/设备告警/工单）' : '（先绑定邮箱后可用）'}
            </Typography.Text>
          </Typography.Text>
          <Typography.Text>角色：{user?.role || '-'}</Typography.Text>
        </Space>
      </Card>
      {/* P3 支付线：套餐与权益（升级/续费入口） */}
      <PlanCard />
      {user?.hasPassword === false && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="当前账号尚未设置密码"
          description="设置密码后可使用手机号+密码登录（当前仅支持短信验证码登录，短信服务不可用时将无法登录）。"
          action={
            <Button size="small" type="primary" ghost onClick={() => setTab('password')} data-testid="go-set-password">
              去设置
            </Button>
          }
        />
      )}
      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'profile',
              label: '个人资料',
              children: <ProfileTab />,
            },
            {
              key: 'orders',
              label: '支付记录',
              children: <PayOrdersTab />,
            },
            {
              key: 'tickets',
              label: '我的工单',
              children: <MyTicketsTab />,
            },
            {
              key: 'coupons',
              label: '我的优惠券',
              children: <MyCouponsTab />,
            },
            {
              key: 'logs',
              label: '在线状态',
              children: <OnlineStatusTab />,
            },
            {
              key: 'sessions',
              label: '登录设备',
              children: <SessionsTab />,
            },
            {
              key: 'phone',
              label: user?.phoneNumber ? '更换绑定手机号' : '绑定手机号',
              children: <ChangePhoneTab />,
            },
            {
              key: 'password',
              label: '设置密码',
              children: <SetPasswordTab />,
            },
            {
              key: 'wechat',
              label: '微信绑定',
              children: (
                <div style={{ maxWidth: 460 }}>
                  {user?.wechatUnionid ? (
                    <>
                      <Space style={{ marginBottom: 12 }}>
                        <WechatOutlined style={{ color: '#07c160' }} />
                        <Typography.Text>已绑定微信：{user.wechatNickname || '微信用户'}</Typography.Text>
                      </Space>
                      <div>
                        <Popconfirm
                          title="解绑后微信扫码将无法直接登录，确认解绑？"
                          okText="解绑"
                          cancelText="取消"
                          onConfirm={handleUnbindWechat}
                        >
                          <Button danger data-testid="wechat-unbind-btn">解绑微信</Button>
                        </Popconfirm>
                      </div>
                    </>
                  ) : (
                    <>
                      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        未绑定微信。绑定后可使用微信扫码快捷登录（已登录状态扫码直接绑定，无需短信）。
                      </Typography.Text>
                      <Button
                        type="primary"
                        icon={<WechatOutlined />}
                        onClick={handleBindWechat}
                        data-testid="wechat-bind-btn"
                      >
                        绑定微信
                      </Button>
                    </>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* T6：绑定/修改联系邮箱（账单/订阅到期/告警通知收件地址） */}
      <Modal
        title={user?.email ? '修改联系邮箱' : '绑定联系邮箱'}
        open={emailOpen}
        onCancel={() => setEmailOpen(false)}
        onOk={() => emailForm.submit()}
        confirmLoading={savingEmail}
        okText="保存"
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          联系邮箱用于接收账单、订阅到期与设备告警通知，不用于登录，可随时修改。
        </Typography.Paragraph>
        <Form form={emailForm} layout="vertical" onFinish={handleSaveEmail}>
          <Form.Item
            name="email"
            label="邮箱地址"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
          >
            <Input placeholder="name@example.com" allowClear data-testid="email-input" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ============================================
// 支付记录 Tab：我的订单（套餐订阅/组件/素材/加购）
// ============================================
const ORDER_TYPE_LABEL: Record<string, string> = {
  subscription: '套餐订阅',
  plugin_component: '组件购买',
  material: '素材购买',
  addon: '增值加购',
};

const ORDER_STATUS_MAP: Record<string, { label: string; color: string }> = {
  pending: { label: '待支付', color: 'orange' },
  paid: { label: '已支付', color: 'blue' },
  fulfilled: { label: '已完成', color: 'green' },
  approved: { label: '已通过', color: 'green' },
  rejected: { label: '已拒绝', color: 'red' },
  refunded: { label: '已退款', color: 'purple' },
  cancelled: { label: '已取消', color: 'default' },
};

function PayOrdersTab() {
  const [items, setItems] = useState<PayOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPayOrders(page, 10);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      // UBS 未连接时静默（显示空列表）
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div data-testid="pay-orders-tab">
      <Table<PayOrder>
        rowKey="orderNo"
        size="middle"
        loading={loading}
        dataSource={items}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }}
        locale={{ emptyText: '暂无支付记录' }}
        columns={[
          { title: '订单号', dataIndex: 'orderNo', width: 220, ellipsis: true },
          {
            title: '类型', dataIndex: 'orderType', width: 110,
            render: (v: string) => ORDER_TYPE_LABEL[v] || v,
          },
          {
            title: '金额', dataIndex: 'amount', width: 110,
            render: (v: number) => <Typography.Text strong>¥{Number(v ?? 0).toFixed(2)}</Typography.Text>,
          },
          {
            title: '状态', dataIndex: 'status', width: 100,
            render: (v: string) => {
              const m = ORDER_STATUS_MAP[v] ?? { label: v, color: 'default' };
              return <Tag color={m.color}>{m.label}</Tag>;
            },
          },
          {
            title: '商品/备注', dataIndex: 'productRef', ellipsis: true,
            render: (v: string | null | undefined) => v || '-',
          },
          {
            title: '创建时间', dataIndex: 'createdAt', width: 170,
            render: (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
          },
          {
            title: '支付时间', dataIndex: 'paidAt', width: 170,
            render: (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
          },
        ]}
      />
    </div>
  );
}